import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { canApprovePO, canRaisePO, formatINR, formatDate, hasCapability, isAdminRole } from "@/lib/permissions";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, FileDown, CheckCircle2, XCircle, Send, Truck, PackageCheck, Pencil, BookOpenCheck } from "lucide-react";
import AssetNameSelect, { isCuratedCategory } from "@/components/AssetNameSelect";
import { BrandInput } from "@/components/BrandInput";
import { useAssetCategories } from "@/hooks/useAssetCategories";

const STATUS_META = {
  draft: { label: "Draft", cls: "border-slate-300 text-slate-600 bg-slate-50" },
  pending_approval: { label: "Pending Approval", cls: "border-amber-300 text-amber-800 bg-amber-50" },
  approved: { label: "Approved", cls: "border-emerald-300 text-emerald-800 bg-emerald-50" },
  rejected: { label: "Rejected", cls: "border-red-300 text-red-700 bg-red-50" },
  issued: { label: "Issued", cls: "border-blue-300 text-blue-800 bg-blue-50" },
  partially_received: { label: "Partially Received", cls: "border-teal-300 text-teal-800 bg-teal-50" },
  received: { label: "Received", cls: "border-indigo-300 text-indigo-800 bg-indigo-50" },
  closed: { label: "Closed", cls: "border-slate-400 text-slate-700 bg-slate-100" },
  cancelled: { label: "Cancelled", cls: "border-rose-400 text-rose-700 bg-rose-50" },
  superseded: { label: "Superseded", cls: "border-orange-300 text-orange-800 bg-orange-50" },
};

const CURRENCY_SYMBOL = { INR: "₹", USD: "$", EUR: "€", CNY: "¥", JPY: "¥" };
const fmtMoney = (n, ccy = "INR") => {
  const sym = CURRENCY_SYMBOL[ccy] || "";
  const val = Number(n || 0);
  const locale = ccy === "INR" ? "en-IN" : "en-US";
  return `${sym}${val.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const { all: assetCategories } = useAssetCategories();
  const [loading, setLoading] = useState(true);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [shortCloseOpen, setShortCloseOpen] = useState(false);
  const [shortCloseReason, setShortCloseReason] = useState("");
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveData, setReceiveData] = useState({
    purchase_date: new Date().toISOString().slice(0, 10),
    installation_date: new Date().toISOString().slice(0, 10),
    serial_number: "",
    warranty_expires: "",
    amc_vendor: "",
    amc_start_date: "",
    amc_end_date: "",
    exchange_rate: "",
    boe_number: "",
    supplier_invoice_no: "",
    supplier_invoice_date: "",
    supplier_invoice_amount: "",
  });
  const [postOpen, setPostOpen] = useState(false);
  const [postExRateOverride, setPostExRateOverride] = useState("");
  const [postAssetSpec, setPostAssetSpec] = useState({
    name: "", category: "Machinery", asset_type: "tangible",
    manufacturer: "", brand: "", model_number: "", location: "",
    useful_life_years: 5, depreciation_method: "SLM", depreciation_rate: 15,
  });
  // Per-unit edits at Post-to-Books time: [{serial_number, purchase_value_inr}]
  const [perUnit, setPerUnit] = useState([]);
  const [busy, setBusy] = useState(false);
  // Per-line "qty received this time" for partial receipts. Key = line.id, value = delta qty.
  // Left blank => defaults to remaining qty (full receipt for the outstanding balance).
  const [lineRecv, setLineRecv] = useState({});
  // Serial numbers being captured IN THIS GRN call — one per unit received.
  // Only relevant when po.po_type === "asset_procurement".
  const [serialsThisReceipt, setSerialsThisReceipt] = useState("");
  // Editable copy of the "for_new_asset" spec that ships with the PO.
  // Seeded from po.for_new_asset when the Receive dialog opens; the stores
  // person may refine model_number / dep method / etc. based on physical goods.
  const [receiveAssetSpec, setReceiveAssetSpec] = useState(null);
  const [receiveSpecTouched, setReceiveSpecTouched] = useState(false);

  // Admin-only rollback tools
  const [rollbackKind, setRollbackKind] = useState(null); // "posting" | "grn" | null
  const [rollbackConfirm, setRollbackConfirm] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/purchase-orders/${id}`);
      setPo(data);
    } catch (e) {
      toast.error("Failed to load PO");
      navigate("/purchase-orders");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const doAction = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/purchase-orders/${id}/${path}`, body || {});
      toast.success("Done");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/purchase-orders/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${po.po_number.replace(/\//g, "_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF download failed"); }
  };

  const downloadGrn = async () => {
    try {
      const res = await api.get(`/purchase-orders/${id}/grn.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `GRN-${po.po_number.replace(/\//g, "-")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("GRN download failed"); }
  };

  const markReceived = async () => {
    const isFcy = (po?.currency || "INR") !== "INR";
    if (isFcy) {
      const ex = Number(receiveData.exchange_rate);
      if (!ex || ex <= 0) {
        toast.error("Exchange rate is required for a foreign-currency PO (from BOE / bank advice).");
        return;
      }
    }
    setBusy(true);
    try {
      // Build received_qty_by_line: for lines where the user left the input blank,
      // fall back to the REMAINING qty (ordered − already-received-cumulative).
      const alreadyRecv = po.received_qty_cumulative_by_line || {};
      const perLineDelta = {};
      (po.line_items || []).forEach((l) => {
        const raw = lineRecv[l.id];
        const remaining = Math.max(0, (Number(l.qty) || 0) - (Number(alreadyRecv[l.id]) || 0));
        let delta;
        if (raw === "" || raw == null) {
          delta = remaining; // default: receive all remaining
        } else {
          delta = Number(raw);
          if (Number.isNaN(delta) || delta < 0) delta = 0;
          if (delta > remaining) delta = remaining;
        }
        if (delta > 0) perLineDelta[l.id] = delta;
      });
      const payload = {
        ...receiveData,
        exchange_rate: receiveData.exchange_rate ? Number(receiveData.exchange_rate) : null,
        supplier_invoice_amount: receiveData.supplier_invoice_amount ? Number(receiveData.supplier_invoice_amount) : null,
        received_qty_by_line: perLineDelta,
      };
      // Attach per-unit serial numbers (asset_procurement only). Parse the
      // textarea by newline / comma so users can paste from a scan gun or Excel.
      if (po.po_type === "asset_procurement") {
        const totalDeltaUnits = Object.values(perLineDelta).reduce(
          (s, v) => s + (Number(v) || 0), 0
        );
        const serials = serialsThisReceipt
          .split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
        if (serials.length > totalDeltaUnits) {
          toast.error(`You entered ${serials.length} serial(s) but this receipt is for ${totalDeltaUnits} unit(s). Please align them.`);
          setBusy(false);
          return;
        }
        payload.serial_numbers = serials;
      }
      // Include edited asset spec in the receipt call — backend accepts
      // for_new_asset on `/mark-received` and merges it with any existing spec
      // from the PO. Only send when the receiver actually edited a field, so
      // untouched receipts don't clobber the spec.
      if (po.po_type === "asset_procurement" && receiveAssetSpec && receiveSpecTouched) {
        payload.for_new_asset = {
          ...receiveAssetSpec,
          useful_life_years: Number(receiveAssetSpec.useful_life_years) || 5,
          depreciation_rate: Number(receiveAssetSpec.depreciation_rate) || 15,
        };
      }
      const { data } = await api.post(`/purchase-orders/${id}/mark-received`, payload);
      const nowPartial = (data && (data.status || "").includes("partial")) ||
        (po.line_items || []).some((l) => {
          const totalRecv = (alreadyRecv[l.id] || 0) + (perLineDelta[l.id] || 0);
          return totalRecv < (Number(l.qty) || 0);
        });
      toast.success(nowPartial
        ? "Partial receipt recorded — you can top up the remaining qty later."
        : "PO fully received. Review and Post to Books to finalise accounting.");
      setReceiveOpen(false);
      setLineRecv({});
      setSerialsThisReceipt("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to mark received");
    } finally { setBusy(false); }
  };

  const needsAssetSpec = po?.po_type === "asset_procurement" && !po?.for_new_asset?.name;

  // Total units to be capitalised (asset_procurement only). Sums the
  // cumulative received qty across all lines. Falls back to ordered qty for
  // legacy POs (pre-serial-ledger).
  const totalUnitsToBook = (() => {
    if (po?.po_type !== "asset_procurement") return 0;
    const cum = po?.received_qty_cumulative_by_line || {};
    const s = Object.values(cum).reduce((acc, v) => acc + (Number(v) || 0), 0);
    if (s > 0) return Math.round(s);
    return (po?.line_items || []).reduce((acc, l) => acc + (Number(l.qty) || 0), 0);
  })();

  const openPostDialog = () => {
    // Seed per-unit rows: default cost = grand_total_inr / N, serial = ledger entry
    if (po.po_type === "asset_procurement" && totalUnitsToBook > 0) {
      const gt = Number(po.grand_total_inr || po.grand_total || 0);
      const defaultCost = totalUnitsToBook > 0 ? Math.round((gt / totalUnitsToBook) * 100) / 100 : gt;
      const ledger = po.serial_ledger || [];
      const rows = [];
      for (let i = 0; i < totalUnitsToBook; i += 1) {
        rows.push({
          serial_number: ledger[i] || "",
          purchase_value_inr: defaultCost,
        });
      }
      setPerUnit(rows);
    } else {
      setPerUnit([]);
    }
    setPostOpen(true);
  };

  const perUnitTotal = perUnit.reduce((s, r) => s + (Number(r.purchase_value_inr) || 0), 0);
  const perUnitTotalMismatch = po?.po_type === "asset_procurement" && perUnit.length > 0 && (
    Math.abs(perUnitTotal - Number(po.grand_total_inr || po.grand_total || 0)) > 1.0
  );

  const postToBooks = async () => {
    // If PO doesn't have an asset spec, force user to fill it now
    if (needsAssetSpec && !postAssetSpec.name.trim()) {
      toast.error("Please enter the Asset Name to capitalise this Capital-Goods PO.");
      return;
    }
    if (perUnitTotalMismatch) {
      toast.error(`Per-unit values (INR ${perUnitTotal.toFixed(2)}) must equal the PO grand total.`);
      return;
    }
    setBusy(true);
    try {
      const payload = {};
      if (postExRateOverride) payload.exchange_rate = Number(postExRateOverride);
      if (needsAssetSpec) {
        payload.for_new_asset = {
          ...postAssetSpec,
          useful_life_years: Number(postAssetSpec.useful_life_years) || 5,
          depreciation_rate: Number(postAssetSpec.depreciation_rate) || 15,
        };
      }
      if (po.po_type === "asset_procurement" && perUnit.length > 0) {
        payload.per_unit = perUnit.map((r) => ({
          serial_number: (r.serial_number || "").trim() || null,
          purchase_value_inr: Number(r.purchase_value_inr) || 0,
        }));
      }
      const { data } = await api.post(`/purchase-orders/${id}/post-to-books`, payload);
      const n = data?.created_asset_count || (data?.created_asset_id ? 1 : 0);
      if (n > 0) {
        toast.success(`Posted to books — ${n} asset record${n > 1 ? "s" : ""} created with QR labels`);
      } else if (data?.gst_register_entries > 0) {
        toast.success(`Posted ${data.gst_register_entries} GST-register entrie(s) for input credit`);
      } else {
        toast.success("PO posted to books");
      }
      setPostOpen(false);
      setPostExRateOverride("");
      setPerUnit([]);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to post to books");
    } finally { setBusy(false); }
  };

  const isAdmin = isAdminRole(user?.role);

  const doRollback = async () => {
    if (!rollbackKind) return;
    const url = rollbackKind === "posting"
      ? `/purchase-orders/${id}/rollback-posting`
      : `/purchase-orders/${id}/rollback-grn`;
    setBusy(true);
    try {
      const { data } = await api.post(url, { confirm_po_number: rollbackConfirm.trim() });
      if (rollbackKind === "posting") {
        toast.success(
          `Rollback complete — ${data.assets_deleted} asset(s) removed, sequence resynced to ${data.asset_seq_reset_to}. PO status: ${data.restored_status}`
        );
      } else {
        toast.success(`GRN rolled back — PO back to Issued. Re-do the GRN using the new multi-serial dialog`);
      }
      setRollbackKind(null);
      setRollbackConfirm("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Rollback failed");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="text-slate-400 text-center py-12">Loading...</div>;
  if (!po) return null;

  const isApprover = hasCapability(user, "approve_po");
  // Feb 2026 — Edit is available while draft or pending_approval. Editing a
  // pending PO resets it back to draft (server side) so the user must re-submit.
  // Approved and Rejected POs must go through Revise (with -REV suffix) to
  // preserve the audit trail per product decision.
  const canEdit = hasCapability(user, "raise_po") && ["draft", "pending_approval"].includes(po.status);
  // Revise is available for approved (no GRN activity) OR rejected POs.
  const rq = po.received_qty_by_line || {};
  const hasGrnActivity = Object.values(rq).some((v) => Number(v || 0) > 0);
  const canRevise = ["approved", "issued", "rejected"].includes(po.status) && !hasGrnActivity && (
    isAdminRole(user?.role) || user?.role === "manager" || user?.id === po.created_by
  );

  const doRevise = async () => {
    if (!confirm("Revise this PO? A new draft copy with a -REV suffix will be created, and this PO will be marked Superseded. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/purchase-orders/${id}/revise`);
      toast.success(`Revision ${data.po_number} created`);
      navigate(`/purchase-orders/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to revise PO");
    } finally {
      setBusy(false);
    }
  };
  const status = po.status;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="po-detail-page">
      <button onClick={() => navigate("/purchase-orders")} className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Back to POs
      </button>

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">Purchase Order</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1 font-mono">{po.po_number}</h1>
          <div className="flex items-center gap-3 mt-2">
            <Badge variant="outline" className={STATUS_META[status]?.cls}>{STATUS_META[status]?.label}</Badge>
            <span className="text-xs text-slate-500">Raised {formatDate(po.created_at)} by {po.created_by_name}</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid="po-download-pdf">
            <FileDown className="w-4 h-4 mr-2" /> Download PO
          </Button>
          {(status === "received" || status === "partially_received" || status === "closed") && (
            <Button variant="outline" onClick={downloadGrn} data-testid="po-download-grn" className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
              <FileDown className="w-4 h-4 mr-2" /> Download GRN
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" onClick={() => navigate(`/purchase-orders/${id}/edit`)} data-testid="edit-po-btn">
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
          )}
          {canRevise && (
            <Button
              variant="outline"
              className="border-orange-300 text-orange-800 hover:bg-orange-50"
              disabled={busy}
              onClick={doRevise}
              data-testid="revise-po-btn"
            >
              <Pencil className="w-4 h-4 mr-2" /> Revise
            </Button>
          )}
          {status === "draft" && hasCapability(user, "raise_po") && (
            <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" disabled={busy} onClick={() => doAction("submit")} data-testid="submit-approval-btn">
              <Send className="w-4 h-4 mr-2" /> Submit for Approval
            </Button>
          )}
          {status === "pending_approval" && isApprover && (
            <>
              <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" disabled={busy} onClick={() => setRejectOpen(true)} data-testid="reject-btn">
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => doAction("approve")} data-testid="approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
              </Button>
            </>
          )}
          {status === "approved" && hasCapability(user, "mark_po_issued") && (
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={busy} onClick={() => doAction("mark-issued")} data-testid="mark-issued-btn">
              <Truck className="w-4 h-4 mr-2" /> Mark Issued
            </Button>
          )}
          {(status === "issued" || status === "partially_received") && hasCapability(user, "receive_po") && (
            <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={busy} onClick={() => {
              // Seed editable copy of the asset spec from the PO for stores to refine
              if (po.po_type === "asset_procurement") {
                setReceiveAssetSpec({
                  name: po.for_new_asset?.name || "",
                  category: po.for_new_asset?.category || "Machinery",
                  asset_type: po.for_new_asset?.asset_type || "tangible",
                  manufacturer: po.for_new_asset?.manufacturer || po.vendor_name || "",
                  brand: po.for_new_asset?.brand || "",
                  model_number: po.for_new_asset?.model_number || "",
                  location: po.for_new_asset?.location || "",
                  useful_life_years: po.for_new_asset?.useful_life_years || 5,
                  depreciation_method: po.for_new_asset?.depreciation_method || "SLM",
                  depreciation_rate: po.for_new_asset?.depreciation_rate || 15,
                });
                setReceiveSpecTouched(false);
              }
              setReceiveOpen(true);
            }} data-testid="mark-received-btn">
              <PackageCheck className="w-4 h-4 mr-2" /> {status === "partially_received" ? "Update Receipt" : "Mark Received"}
            </Button>
          )}
          {/* Cancel PO — after approval, before any goods received. Any PO type. */}
          {(status === "approved" || status === "issued")
            && hasCapability(user, "cancel_po")
            && !Object.values(po.received_qty_cumulative_by_line || po.received_qty_by_line || {}).some((v) => Number(v) > 0) && (
            <Button
              variant="outline"
              className="border-rose-400 text-rose-700 hover:bg-rose-50"
              disabled={busy}
              onClick={() => { setCancelReason(""); setCancelOpen(true); }}
              data-testid="cancel-po-btn"
              title="Cancel this PO before any goods are received"
            >
              <XCircle className="w-4 h-4 mr-2" /> Cancel PO
            </Button>
          )}
          {/* Short Close — only for partially-received Consumables POs */}
          {status === "partially_received"
            && po.po_type === "consumables_purchase"
            && hasCapability(user, "short_close_po") && (
            <Button
              variant="outline"
              className="border-amber-400 text-amber-800 hover:bg-amber-50"
              disabled={busy}
              onClick={() => { setShortCloseReason(""); setShortCloseOpen(true); }}
              data-testid="short-close-btn"
              title="Accept the partial delivery as final and close this PO"
            >
              <XCircle className="w-4 h-4 mr-2" /> Short Close
            </Button>
          )}
          {status === "received" && !po.posted_to_books && hasCapability(user, "post_po_to_books") && (
            <Button className="bg-purple-700 hover:bg-purple-800" disabled={busy} onClick={openPostDialog} data-testid="post-to-books-btn">
              <BookOpenCheck className="w-4 h-4 mr-2" /> Post to Books
            </Button>
          )}
          {status === "received" && po.posted_to_books && isApprover && (
            <Button variant="outline" disabled={busy} onClick={() => doAction("close")} data-testid="close-po-btn">
              Close PO
            </Button>
          )}
          {isAdmin && po.posted_to_books && (
            <Button
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              disabled={busy}
              onClick={() => { setRollbackKind("posting"); setRollbackConfirm(""); }}
              data-testid="rollback-posting-btn"
              title="Undo posting to books — deletes auto-created asset(s) and resyncs the asset sequence counter"
            >
              Rollback Posting
            </Button>
          )}
          {isAdmin && !po.posted_to_books && (
            ["received", "partially_received"].includes(status)
            || (status === "issued" && (
              (Array.isArray(po.serial_ledger) && po.serial_ledger.length > 0)
              || po.receipt_spec
              || po.received_qty_by_line
              || po.received_qty_cumulative_by_line
              || po.received_at
            ))
          ) && (
            <Button
              variant="outline"
              className="border-red-300 text-red-700 hover:bg-red-50"
              disabled={busy}
              onClick={() => { setRollbackKind("grn"); setRollbackConfirm(""); }}
              data-testid="rollback-grn-btn"
              title="Undo GRN — clears serial ledger and receipt spec, PO returns to Issued"
            >
              Rollback GRN
            </Button>
          )}
        </div>
      </div>

      {po.rejection_reason && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-800">
            <span className="font-semibold">Rejection reason:</span> {po.rejection_reason}
          </CardContent>
        </Card>
      )}

      {status === "cancelled" && (
        <Card className="border-rose-300 bg-rose-50" data-testid="cancelled-banner">
          <CardContent className="p-4 text-sm text-rose-900 flex items-start gap-3">
            <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">PO Cancelled</div>
              <div className="text-xs mt-0.5">
                Cancelled by <b>{po.cancelled_by_name || "—"}</b>
                {po.cancelled_at && <> on {new Date(po.cancelled_at).toLocaleString("en-IN")}</>}
                {po.prev_status_before_cancel && <> (was <i>{po.prev_status_before_cancel}</i>)</>}
                . <span className="font-medium">Reason:</span> {po.cancellation_reason || "—"}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {po.short_closed && (
        <Card className="border-amber-300 bg-amber-50" data-testid="short-closed-banner">
          <CardContent className="p-4 text-sm text-amber-900 flex items-start gap-3">
            <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">Short-Closed</div>
              <div className="text-xs mt-0.5">
                Partial delivery accepted as final by <b>{po.short_closed_by_name || "—"}</b>
                {po.short_closed_at && <> on {new Date(po.short_closed_at).toLocaleString("en-IN")}</>}
                . <span className="font-medium">Reason:</span> {po.short_close_reason || "—"}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {po.auto_closed && !po.short_closed && (
        <Card className="border-emerald-300 bg-emerald-50" data-testid="auto-closed-banner">
          <CardContent className="p-4 text-sm text-emerald-900 flex items-start gap-3">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-semibold">Auto-Closed on Full Receipt</div>
              <div className="text-xs mt-0.5">
                Fully received by <b>{(po.closed_by_name || "").replace(" (auto)", "") || "—"}</b>
                {po.closed_at && <> on {new Date(po.closed_at).toLocaleString("en-IN")}</>}.
                {" "}Stock inward posted, GST input credit registered
                ({(po.gst_register_entry_ids || []).length} entr{(po.gst_register_entry_ids || []).length === 1 ? "y" : "ies"}),
                and the file was closed automatically. No further edits are possible.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {po.superseded_by && (
        <Card className="border-orange-200 bg-orange-50" data-testid="superseded-banner">
          <CardContent className="p-4 text-sm text-orange-900 flex items-center gap-3">
            <XCircle className="w-4 h-4 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold">This PO has been superseded</div>
              <div className="text-xs mt-0.5">
                Revised as{" "}
                <button
                  onClick={() => navigate(`/purchase-orders/${po.superseded_by}`)}
                  className="font-mono font-semibold underline hover:text-orange-700"
                  data-testid="superseded-goto-rev-btn"
                >
                  {po.superseded_by_number || "(open revision)"}
                </button>
                . This document is retained for audit purposes only.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {po.revision_of && (
        <Card className="border-blue-200 bg-blue-50" data-testid="revision-banner">
          <CardContent className="p-4 text-sm text-blue-900 flex items-center gap-3">
            <Pencil className="w-4 h-4 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold">
                Revision {po.revision_number} of{" "}
                <button
                  onClick={() => navigate(`/purchase-orders/${po.revision_of}`)}
                  className="font-mono font-semibold underline hover:text-blue-700"
                  data-testid="revision-goto-original-btn"
                >
                  original PO
                </button>
              </div>
              <div className="text-xs mt-0.5">
                This is revision #{po.revision_number} — a new draft that supersedes the earlier approved version.
                Submit for approval once your edits are complete.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Details grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Vendor</CardTitle></CardHeader>
          <CardContent className="p-4 space-y-1 text-sm">
            <div className="font-semibold text-slate-900">{po.vendor_name}</div>
            {po.vendor_gstin && <div className="text-slate-500 font-mono text-xs">GSTIN: {po.vendor_gstin}</div>}
            {po.vendor_address && <div className="text-slate-600 whitespace-pre-wrap">{po.vendor_address}</div>}
            {po.vendor_email && <div className="text-slate-600">📧 {po.vendor_email}</div>}
            {po.vendor_phone && <div className="text-slate-600">📞 {po.vendor_phone}</div>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">PO Details</CardTitle></CardHeader>
          <CardContent className="p-4 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-slate-500">Type</span><span>{po.po_type === "asset_procurement" ? "Fixed Asset (CG)" : po.po_type === "consumables_purchase" ? "Consumables (CON)" : "Machine Spares & Jigs (SPR)"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">PO Date</span><span>{formatDate(po.po_date)}</span></div>
            <div className="flex justify-between">
              <span className="text-slate-500">Currency</span>
              <span className="font-mono font-semibold">{CURRENCY_SYMBOL[po.currency || "INR"]} {po.currency || "INR"}</span>
            </div>
            {po.currency && po.currency !== "INR" && (
              <div className="flex justify-between">
                <span className="text-slate-500">Exchange Rate</span>
                <span className="font-mono">{po.exchange_rate ? `${po.exchange_rate.toFixed(4)} / ${po.currency}` : "— (captured at inward)"}</span>
              </div>
            )}
            {po.boe_number && (
              <div className="flex justify-between"><span className="text-slate-500">BOE No.</span><span className="font-mono text-xs">{po.boe_number}</span></div>
            )}
            {po.supplier_invoice_no && (
              <div className="flex justify-between"><span className="text-slate-500">Supplier Invoice</span><span className="font-mono text-xs">{po.supplier_invoice_no}</span></div>
            )}
            <div className="flex justify-between"><span className="text-slate-500">Expected Delivery</span><span>{po.expected_delivery_date ? formatDate(po.expected_delivery_date) : "—"}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Payment Terms</span><span>{po.payment_terms || "—"}</span></div>
            {po.linked_asset_id && !po.linked_assets_snapshot?.length && (
              <div className="flex justify-between"><span className="text-slate-500">Linked Asset</span>
                <button className="text-[#7C1D3A] hover:underline" onClick={() => navigate(`/assets/${po.linked_asset_id}`)}>View Asset →</button>
              </div>
            )}
            {(po.linked_assets_snapshot?.length > 0 || po.linked_machine_types?.length > 0) && (
              <div className="flex justify-between gap-3" data-testid="po-detail-linked-machines">
                <span className="text-slate-500 shrink-0">Linked Machines</span>
                <div className="flex flex-wrap justify-end gap-1">
                  {(po.linked_machine_types || []).map((t) => (
                    <span key={t} className="text-xs px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-800">All {t}</span>
                  ))}
                  {(po.linked_assets_snapshot || []).map((a) => (
                    <button key={a.id} className="text-xs text-[#7C1D3A] hover:underline font-mono" onClick={() => navigate(`/assets/${a.id}`)} data-testid={`po-detail-linked-${a.asset_id}`}>
                      {a.asset_id}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {po.approved_by_name && (
              <div className="flex justify-between"><span className="text-slate-500">Approved by</span><span className="text-emerald-700 font-medium">{po.approved_by_name}</span></div>
            )}
            {po.posted_to_books && (
              <div className="flex justify-between"><span className="text-slate-500">Posted to Books</span><span className="text-purple-700 font-medium">{po.posted_to_books_by_name} · {formatDate(po.posted_to_books_at)}</span></div>
            )}
            {po.supplier_invoice_amount !== undefined && po.supplier_invoice_amount !== null && (
              <div className="flex justify-between">
                <span className="text-slate-500">Invoice Amount</span>
                <span className="font-mono">
                  {CURRENCY_SYMBOL[po.currency || "INR"]}{Number(po.supplier_invoice_amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {(() => {
                    const variance = Number(po.supplier_invoice_amount) - Number(po.grand_total || 0);
                    if (Math.abs(variance) < 0.5) return <span className="ml-2 text-emerald-700 text-xs">✓ matches PO</span>;
                    return <span className="ml-2 text-red-600 text-xs">Δ {variance > 0 ? "+" : ""}{variance.toFixed(2)}</span>;
                  })()}
                </span>
              </div>
            )}
            {po.created_asset_id && (
              <div className="flex justify-between"><span className="text-slate-500">Created Asset</span>
                <button className="text-[#7C1D3A] hover:underline" onClick={() => navigate(`/assets/${po.created_asset_id}`)}>Open Asset →</button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Line items */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-base">
            Line Items <span className="text-xs font-normal text-slate-500 ml-1">· values in {po.currency || "INR"}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-slate-500 font-semibold text-xs uppercase tracking-wider">
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">HS</th>
                <th className="px-4 py-3 text-right">Qty</th>
                <th className="px-4 py-3">UOM</th>
                <th className="px-4 py-3 text-right">Unit</th>
                <th className="px-4 py-3 text-right">GST %</th>
                <th className="px-4 py-3">Treatment</th>
                <th className="px-4 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(po.line_items || []).map((l, i) => (
                <tr key={l.id || `li-${i}`}>
                  <td className="px-4 py-3 text-slate-500">{i + 1}</td>
                  <td className="px-4 py-3 text-slate-900 whitespace-pre-line">{l.description}</td>
                  <td className="px-4 py-3 font-mono text-xs">{l.hs_code || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{l.qty}</td>
                  <td className="px-4 py-3 text-xs">{l.uom || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{fmtMoney(l.unit_price, po.currency)}</td>
                  <td className="px-4 py-3 text-right font-mono">{l.gst_percent}%</td>
                  <td className="px-4 py-3 text-xs">{l.gst_treatment || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{fmtMoney(l.line_total, po.currency)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-slate-200">
              <tr><td colSpan={8} className="px-4 py-2 text-right text-slate-500">Line items subtotal</td><td className="px-4 py-2 text-right font-mono">{fmtMoney(po.subtotal, po.currency)}</td></tr>
              {Number(po.discount_amount) > 0 && (
                <>
                  <tr><td colSpan={8} className="px-4 py-2 text-right text-slate-500">Less: Discount{po.discount_type === "percent" ? ` (${po.discount_value}%)` : ""}</td><td className="px-4 py-2 text-right font-mono text-red-600">-{fmtMoney(po.discount_amount, po.currency)}</td></tr>
                  <tr><td colSpan={8} className="px-4 py-2 text-right text-slate-500">Taxable base</td><td className="px-4 py-2 text-right font-mono">{fmtMoney(po.taxable_base, po.currency)}</td></tr>
                </>
              )}
              {(po.additional_charges || []).map((ch, i) => (
                <tr key={`ac-${i}`} className="bg-amber-50/60">
                  <td colSpan={8} className="px-4 py-2 text-right text-slate-500 text-xs">
                    [{(ch.charge_type || "other").replace("_", " ")}] {ch.label || (ch.charge_type || "").toString().replace("_", " ")}{ch.apply_gst ? ` · +GST ${ch.gst_percent}%` : ""}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{fmtMoney(ch.line_total, po.currency)}</td>
                </tr>
              ))}
              <tr><td colSpan={8} className="px-4 py-2 text-right text-slate-500">Total GST</td><td className="px-4 py-2 text-right font-mono">{fmtMoney(po.total_gst, po.currency)}</td></tr>
              <tr className="bg-[#fcf0f3]"><td colSpan={8} className="px-4 py-3 text-right font-semibold text-[#7C1D3A]">Grand Total</td><td className="px-4 py-3 text-right font-mono font-bold text-[#7C1D3A]">{fmtMoney(po.grand_total, po.currency)}</td></tr>
              {po.grand_total_inr && (po.currency || "INR") !== "INR" && (
                <tr className="bg-purple-50">
                  <td colSpan={8} className="px-4 py-2 text-right text-purple-800 text-xs">
                    INR landed @ {po.exchange_rate?.toFixed(4)} · Base {fmtMoney(po.subtotal_inr, "INR")} + GST {fmtMoney(po.total_gst_inr, "INR")}
                  </td>
                  <td className="px-4 py-2 text-right font-mono font-bold text-purple-800">{fmtMoney(po.grand_total_inr, "INR")}</td>
                </tr>
              )}
            </tfoot>
          </table>
        </CardContent>
      </Card>

      {po.for_new_asset && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">New Asset (will be auto-created on receipt)</CardTitle></CardHeader>
          <CardContent className="p-4 grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-slate-500">Name:</span> <span className="text-slate-900 font-medium">{po.for_new_asset.name}</span></div>
            <div><span className="text-slate-500">Category:</span> <span className="text-slate-900">{po.for_new_asset.category}</span></div>
            {po.for_new_asset.manufacturer && <div><span className="text-slate-500">Invoicing Party:</span> {po.for_new_asset.manufacturer}</div>}
            {po.for_new_asset.brand && <div><span className="text-slate-500">Brand:</span> {po.for_new_asset.brand}</div>}
            {po.for_new_asset.model_number && <div><span className="text-slate-500">Model:</span> {po.for_new_asset.model_number}</div>}
            {po.for_new_asset.location && <div><span className="text-slate-500">Location:</span> {po.for_new_asset.location}</div>}
            <div><span className="text-slate-500">Useful life:</span> {po.for_new_asset.useful_life_years} years</div>
            <div><span className="text-slate-500">Method:</span> {po.for_new_asset.depreciation_method}</div>
          </CardContent>
        </Card>
      )}

      {po.notes && (
        <Card>
          <CardHeader><CardTitle className="font-heading text-base">Notes</CardTitle></CardHeader>
          <CardContent className="p-4 text-sm text-slate-700 whitespace-pre-wrap">{po.notes}</CardContent>
        </Card>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Reject Purchase Order</DialogTitle></DialogHeader>
          <div>
            <Label>Reason for rejection *</Label>
            <Textarea rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={!rejectReason.trim() || busy} onClick={async () => { await doAction("reject", { reason: rejectReason }); setRejectOpen(false); setRejectReason(""); }}>
              Reject PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel PO Dialog (post-approval, pre-receipt) */}
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-rose-800">Cancel Purchase Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900">
              This will cancel <b>{po.po_number}</b> (currently <i>{status}</i>). Cancelled POs cannot be reactivated —
              raise a fresh PO if the order needs to proceed. If this PO was raised from a Consumable Requisition,
              the requisition will be reverted to <b>approved</b> so a new PO can be raised against it.
            </div>
            <div>
              <Label>Reason for cancellation *</Label>
              <Textarea
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g. Vendor unable to deliver; raising fresh PO with alt supplier"
                data-testid="cancel-po-reason"
              />
              <div className="text-[11px] text-slate-500 mt-1">Minimum 5 characters.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelOpen(false)}>Keep PO</Button>
            <Button
              variant="destructive"
              disabled={cancelReason.trim().length < 5 || busy}
              onClick={async () => {
                await doAction("cancel", { reason: cancelReason.trim() });
                setCancelOpen(false);
                setCancelReason("");
              }}
              data-testid="cancel-po-confirm-btn"
            >
              Cancel PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Short Close Dialog (consumables only, partially_received) */}
      <Dialog open={shortCloseOpen} onOpenChange={setShortCloseOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading text-amber-800">Short Close Consumables PO</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              Accept the partial delivery on <b>{po.po_number}</b> as the final receipt. Received stock stays in inventory,
              the remaining ordered qty is dropped, and the PO moves to <b>closed</b>. Reports will continue to show
              <i> received / ordered</i> so the shortfall is auditable.
            </div>
            <div>
              <Label>Reason for short close *</Label>
              <Textarea
                rows={3}
                value={shortCloseReason}
                onChange={(e) => setShortCloseReason(e.target.value)}
                placeholder="e.g. Vendor supplied 88 of 100; balance not required"
                data-testid="short-close-reason"
              />
              <div className="text-[11px] text-slate-500 mt-1">Minimum 5 characters.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShortCloseOpen(false)}>Back</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              disabled={shortCloseReason.trim().length < 5 || busy}
              onClick={async () => {
                await doAction("short-close", { reason: shortCloseReason.trim() });
                setShortCloseOpen(false);
                setShortCloseReason("");
              }}
              data-testid="short-close-confirm-btn"
            >
              Short Close PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Dialog */}
      <Dialog open={receiveOpen} onOpenChange={setReceiveOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark PO as Received</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {(po.currency || "INR") !== "INR" && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-amber-900 text-xs">
                Foreign-currency PO ({po.currency}). Enter the <b>BOE / bank-advice exchange rate</b> — the landed INR value and GST will be calculated at this rate.
              </div>
            )}
            {po.po_type === "asset_procurement" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-emerald-800 text-xs">
                On <b>Post to Books</b>, a new asset &quot;{receiveAssetSpec?.name || po.for_new_asset?.name}&quot; will be capitalised at the full landed cost (Base INR + GST INR).
              </div>
            )}
            {po.po_type === "asset_procurement" && receiveAssetSpec && (
              <div className="border border-slate-300 rounded-md p-3 bg-white" data-testid="receive-asset-spec-block">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                    Asset details (auto-created on Post to Books)
                  </Label>
                  <span className="text-[10px] text-slate-400 italic">
                    Refine as per the physical goods received
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <Label className="text-[10px] text-slate-500">Asset Name *</Label>
                    <AssetNameSelect
                      category={receiveAssetSpec.category}
                      value={receiveAssetSpec.name || ""}
                      onChange={(v) => { setReceiveAssetSpec({ ...receiveAssetSpec, name: v }); setReceiveSpecTouched(true); }}
                      testid="receive-spec-name"
                      placeholder={
                        isCuratedCategory(receiveAssetSpec.category)
                          ? `Select ${(receiveAssetSpec.category || "").toLowerCase()}…`
                          : "e.g. Juki DDL-8100e single-needle sewing machine"
                      }
                    />
                    {isCuratedCategory(receiveAssetSpec.category) && (
                      <p className="text-[10px] text-slate-500 mt-1">
                        Pick from the {receiveAssetSpec.category} list or click <b>+ Add new</b> — the app will warn on caps / typo duplicates.
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Invoicing Party</Label>
                    <Input
                      value={receiveAssetSpec.manufacturer || ""}
                      onChange={(e) => { setReceiveAssetSpec({ ...receiveAssetSpec, manufacturer: e.target.value }); setReceiveSpecTouched(true); }}
                      className="h-9 text-sm"
                      data-testid="receive-spec-invoicing-party"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Brand</Label>
                    <BrandInput
                      value={receiveAssetSpec.brand || ""}
                      onChange={(v) => { setReceiveAssetSpec({ ...receiveAssetSpec, brand: v }); setReceiveSpecTouched(true); }}
                      className="h-9 text-sm"
                      testid="receive-spec-brand"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Model Number</Label>
                    <Input
                      value={receiveAssetSpec.model_number || ""}
                      onChange={(e) => { setReceiveAssetSpec({ ...receiveAssetSpec, model_number: e.target.value }); setReceiveSpecTouched(true); }}
                      className="h-9 text-sm"
                      data-testid="receive-spec-model"
                      placeholder="Match the label on the machine"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Location</Label>
                    <Input
                      value={receiveAssetSpec.location || ""}
                      onChange={(e) => { setReceiveAssetSpec({ ...receiveAssetSpec, location: e.target.value }); setReceiveSpecTouched(true); }}
                      className="h-9 text-sm"
                      data-testid="receive-spec-location"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Useful Life (years)</Label>
                    <Input
                      type="number" min="1" step="1"
                      value={receiveAssetSpec.useful_life_years || ""}
                      onChange={(e) => { setReceiveAssetSpec({ ...receiveAssetSpec, useful_life_years: e.target.value }); setReceiveSpecTouched(true); }}
                      className="h-9 text-sm"
                      data-testid="receive-spec-useful-life"
                    />
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Depreciation Method</Label>
                    <select
                      className="h-9 w-full rounded-md border border-slate-300 px-3 text-sm bg-white"
                      value={receiveAssetSpec.depreciation_method || "SLM"}
                      onChange={(e) => { setReceiveAssetSpec({ ...receiveAssetSpec, depreciation_method: e.target.value }); setReceiveSpecTouched(true); }}
                      data-testid="receive-spec-dep-method"
                    >
                      <option value="SLM">SLM (Straight Line)</option>
                      <option value="WDV">WDV (Written Down Value)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-[10px] text-slate-500">Depreciation Rate (%)</Label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={receiveAssetSpec.depreciation_rate || ""}
                      onChange={(e) => { setReceiveAssetSpec({ ...receiveAssetSpec, depreciation_rate: e.target.value }); setReceiveSpecTouched(true); }}
                      className="h-9 text-sm"
                      data-testid="receive-spec-dep-rate"
                    />
                  </div>
                </div>
                {receiveSpecTouched && (
                  <p className="text-[10px] text-emerald-700 mt-2 flex items-center gap-1">
                    <PackageCheck className="w-3 h-3" /> Changes will be saved with this GRN and used when the asset is capitalised.
                  </p>
                )}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              {/* Per-line receipt qty */}
              <div className="col-span-2 border border-slate-200 rounded-md p-3 bg-slate-50/50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Qty received this time (per line)</Label>
                  <span className="text-[10px] text-slate-400">Leave blank to receive the full remaining qty</span>
                </div>
                <div className="space-y-1.5">
                  {(po.line_items || []).map((l) => {
                    const alreadyRecv = Number((po.received_qty_cumulative_by_line || {})[l.id] || 0);
                    const remaining = Math.max(0, (Number(l.qty) || 0) - alreadyRecv);
                    return (
                      <div key={l.id} className="grid grid-cols-12 gap-2 items-center text-xs">
                        <div className="col-span-6 truncate text-slate-800" title={l.description}>{l.description}</div>
                        <div className="col-span-2 text-right text-slate-500 font-mono">Ord {l.qty}</div>
                        <div className="col-span-2 text-right text-slate-500 font-mono" title="Already received">Rcv {alreadyRecv}</div>
                        <div className="col-span-2">
                          <Input
                            type="number" min="0" step="0.01" max={remaining}
                            placeholder={remaining.toString()}
                            disabled={remaining <= 0}
                            value={lineRecv[l.id] ?? ""}
                            onChange={(e) => setLineRecv({ ...lineRecv, [l.id]: e.target.value })}
                            className="h-8 text-right text-xs"
                            data-testid={`receive-line-qty-${l.id}`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {(po.currency || "INR") !== "INR" && (
                <>
                  <div>
                    <Label>Exchange Rate ({po.currency} → INR) *</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      min="0"
                      value={receiveData.exchange_rate}
                      onChange={(e) => setReceiveData({ ...receiveData, exchange_rate: e.target.value })}
                      placeholder="e.g. 83.42"
                      data-testid="po-exchange-rate"
                    />
                  </div>
                  <div>
                    <Label>BOE / Bank Advice No.</Label>
                    <Input
                      value={receiveData.boe_number}
                      onChange={(e) => setReceiveData({ ...receiveData, boe_number: e.target.value })}
                      placeholder="e.g. 1234567"
                      data-testid="po-boe-number"
                    />
                  </div>
                </>
              )}
              <div>
                <Label>Supplier Invoice No.</Label>
                <Input value={receiveData.supplier_invoice_no} onChange={(e) => setReceiveData({ ...receiveData, supplier_invoice_no: e.target.value })} data-testid="po-supplier-invoice-no" />
              </div>
              <div>
                <Label>Supplier Invoice Date</Label>
                <Input type="date" value={receiveData.supplier_invoice_date} onChange={(e) => setReceiveData({ ...receiveData, supplier_invoice_date: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Supplier Invoice Amount <span className="text-slate-400 text-[11px]">(in {po.currency || "INR"} — for 3-way match)</span></Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={receiveData.supplier_invoice_amount}
                  onChange={(e) => setReceiveData({ ...receiveData, supplier_invoice_amount: e.target.value })}
                  placeholder={`e.g. ${(po.grand_total || 0).toFixed(2)} — leave blank if invoice not yet received`}
                  data-testid="po-supplier-invoice-amount"
                />
              </div>
              {po.po_type === "asset_procurement" && (
                <>
                  <div><Label>Purchase Date *</Label><Input type="date" value={receiveData.purchase_date} onChange={(e) => setReceiveData({ ...receiveData, purchase_date: e.target.value })} /></div>
                  <div><Label>Installation Date *</Label><Input type="date" value={receiveData.installation_date} onChange={(e) => setReceiveData({ ...receiveData, installation_date: e.target.value })} /></div>
                  <div className="col-span-2">
                    <Label>Serial Numbers <span className="text-slate-400 text-[11px]">— one per unit (newline or comma separated). Leave blank to fill later at Post-to-Books.</span></Label>
                    <textarea
                      className="w-full min-h-[80px] rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#7C1D3A]/30"
                      value={serialsThisReceipt}
                      onChange={(e) => setSerialsThisReceipt(e.target.value)}
                      placeholder={"SN-2026-0001\nSN-2026-0002\nSN-2026-0003"}
                      data-testid="po-serial-numbers"
                    />
                    {(() => {
                      const alreadyRecv = po.received_qty_cumulative_by_line || {};
                      const totalDelta = (po.line_items || []).reduce((s, l) => {
                        const raw = lineRecv[l.id];
                        const remaining = Math.max(0, (Number(l.qty) || 0) - (Number(alreadyRecv[l.id]) || 0));
                        const d = raw === "" || raw == null ? remaining : Math.min(Math.max(0, Number(raw) || 0), remaining);
                        return s + d;
                      }, 0);
                      const entered = serialsThisReceipt.split(/[\n,]/).map((x) => x.trim()).filter(Boolean).length;
                      return (
                        <div className={`mt-1 text-[11px] ${entered > totalDelta ? "text-red-600" : "text-slate-500"}`}>
                          {entered} / {totalDelta} serial{totalDelta === 1 ? "" : "s"} entered for this receipt
                        </div>
                      );
                    })()}
                  </div>
                  <div><Label>Warranty Expires</Label><Input type="date" value={receiveData.warranty_expires} onChange={(e) => setReceiveData({ ...receiveData, warranty_expires: e.target.value })} /></div>
                  <div><Label>AMC Vendor</Label><Input value={receiveData.amc_vendor} onChange={(e) => setReceiveData({ ...receiveData, amc_vendor: e.target.value })} /></div>
                  <div><Label>AMC Start</Label><Input type="date" value={receiveData.amc_start_date} onChange={(e) => setReceiveData({ ...receiveData, amc_start_date: e.target.value })} /></div>
                  <div><Label>AMC End</Label><Input type="date" value={receiveData.amc_end_date} onChange={(e) => setReceiveData({ ...receiveData, amc_end_date: e.target.value })} /></div>
                </>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" disabled={busy} onClick={markReceived} data-testid="confirm-receive-btn">
              {busy ? "Processing..." : "Confirm Receipt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post to Books Dialog */}
      <Dialog open={postOpen} onOpenChange={setPostOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">Post PO to Books</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {po.po_type === "asset_procurement" ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-md p-3 text-emerald-900 text-xs">
                <b>Capital Goods:</b> Landed cost (Base INR + GST INR) will be capitalised as the asset value.
                {po.for_new_asset?.name
                  ? <> A new asset (<b>{po.for_new_asset.name}</b>) will be created.</>
                  : <> Please enter the asset details below to book this Capital-Goods PO.</>}
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-blue-900 text-xs">
                <b>Revenue PO:</b> Only the Base INR value is stock/expense. Each GST-bearing line will be posted to the <b>GST Input Register</b> for downstream input credit claim.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>Currency: <b>{po.currency || "INR"}</b></div>
              <div>Exchange rate captured: <b className="font-mono">{po.exchange_rate?.toFixed?.(4) || "1.0000"}</b></div>
              <div>Base ({po.currency}): <b className="font-mono">{fmtMoney(po.subtotal, po.currency)}</b></div>
              <div>GST ({po.currency}): <b className="font-mono">{fmtMoney(po.total_gst, po.currency)}</b></div>
            </div>

            {needsAssetSpec && (
              <div className="border border-emerald-300 bg-emerald-50/40 rounded-md p-3 space-y-3">
                <div className="text-xs font-semibold text-emerald-900 uppercase tracking-wider">Asset Details (required)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2">
                    <Label>Asset Name *</Label>
                    <AssetNameSelect
                      category={postAssetSpec.category}
                      value={postAssetSpec.name}
                      onChange={(v) => setPostAssetSpec({ ...postAssetSpec, name: v })}
                      testid="post-asset-name"
                      placeholder="e.g. CNC Laser TruLaser 3030"
                    />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <select
                      value={postAssetSpec.category}
                      onChange={(e) => setPostAssetSpec({ ...postAssetSpec, category: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
                      data-testid="post-asset-category"
                    >
                      {assetCategories.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input value={postAssetSpec.location} onChange={(e) => setPostAssetSpec({ ...postAssetSpec, location: e.target.value })} />
                  </div>
                  <div>
                    <Label>Invoicing Party</Label>
                    <Input value={postAssetSpec.manufacturer} onChange={(e) => setPostAssetSpec({ ...postAssetSpec, manufacturer: e.target.value })} data-testid="post-asset-invoicing-party" />
                  </div>
                  <div>
                    <Label>Brand</Label>
                    <BrandInput value={postAssetSpec.brand || ""} onChange={(v) => setPostAssetSpec({ ...postAssetSpec, brand: v })} testid="post-asset-brand" />
                  </div>
                  <div>
                    <Label>Model Number</Label>
                    <Input value={postAssetSpec.model_number} onChange={(e) => setPostAssetSpec({ ...postAssetSpec, model_number: e.target.value })} />
                  </div>
                  <div>
                    <Label>Useful Life (years)</Label>
                    <Input type="number" value={postAssetSpec.useful_life_years} onChange={(e) => setPostAssetSpec({ ...postAssetSpec, useful_life_years: e.target.value })} />
                  </div>
                  <div>
                    <Label>Depreciation</Label>
                    <select
                      value={postAssetSpec.depreciation_method}
                      onChange={(e) => setPostAssetSpec({ ...postAssetSpec, depreciation_method: e.target.value })}
                      className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm"
                    >
                      <option value="SLM">Straight-Line (SLM)</option>
                      <option value="WDV">Written-Down Value (WDV)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label>Override exchange rate (optional)</Label>
              <Input
                type="number"
                step="0.0001"
                min="0"
                value={postExRateOverride}
                onChange={(e) => setPostExRateOverride(e.target.value)}
                placeholder={`Leave blank to use ${po.exchange_rate?.toFixed?.(4) || "1.0000"}`}
                data-testid="post-exchange-rate-override"
              />
              <div className="text-[11px] text-slate-500 mt-1">Only use if the accounts team has agreed on a different final rate.</div>
            </div>

            {po.po_type === "asset_procurement" && perUnit.length > 0 && (
              <div className="border border-slate-200 rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Assets to create ({perUnit.length})
                    </div>
                    <div className="text-[11px] text-slate-500">
                      One asset record + QR label per unit. Cost split equally by default — edit any row to override, totals must match the PO grand total.
                    </div>
                  </div>
                  <div className={`text-xs font-mono ${perUnitTotalMismatch ? "text-red-600 font-bold" : "text-slate-600"}`}>
                    Σ INR {perUnitTotal.toFixed(2)} / {Number(po.grand_total_inr || po.grand_total || 0).toFixed(2)}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500 text-[11px] uppercase tracking-wider border-b border-slate-200">
                        <th className="text-left py-1 pr-2 w-8">#</th>
                        <th className="text-left py-1 pr-2">Serial Number</th>
                        <th className="text-right py-1 pl-2">Unit Value (INR)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {perUnit.map((row, idx) => (
                        <tr key={idx}>
                          <td className="py-1.5 pr-2 text-slate-400 font-mono">{idx + 1}</td>
                          <td className="py-1.5 pr-2">
                            <Input
                              className="h-8 text-xs font-mono"
                              value={row.serial_number}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPerUnit((prev) => prev.map((r, i) => i === idx ? { ...r, serial_number: v } : r));
                              }}
                              placeholder={`Unit ${idx + 1} serial`}
                              data-testid={`post-unit-${idx}-serial`}
                            />
                          </td>
                          <td className="py-1.5 pl-2">
                            <Input
                              className="h-8 text-xs font-mono text-right"
                              type="number"
                              step="0.01"
                              min="0"
                              value={row.purchase_value_inr}
                              onChange={(e) => {
                                const v = e.target.value;
                                setPerUnit((prev) => prev.map((r, i) => i === idx ? { ...r, purchase_value_inr: v } : r));
                              }}
                              data-testid={`post-unit-${idx}-value`}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {perUnitTotalMismatch && (
                  <div className="text-[11px] text-red-600 font-medium">
                    Unit totals must equal the PO grand total (INR {Number(po.grand_total_inr || po.grand_total || 0).toFixed(2)}) before you can post.
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPostOpen(false)}>Cancel</Button>
            <Button className="bg-purple-700 hover:bg-purple-800" disabled={busy} onClick={postToBooks} data-testid="confirm-post-to-books-btn">
              {busy ? "Posting..." : "Post to Books"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin: Rollback confirmation dialog */}
      <Dialog open={!!rollbackKind} onOpenChange={(o) => { if (!o) { setRollbackKind(null); setRollbackConfirm(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-700">
              {rollbackKind === "posting" ? "Rollback Posting to Books" : "Rollback GRN"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-red-900 text-xs">
              <div className="font-semibold mb-1">This action is irreversible.</div>
              {rollbackKind === "posting" ? (
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Deletes {po.created_asset_ids?.length || (po.created_asset_id ? 1 : 0)} auto-created asset record(s) and all their maintenance schedules</li>
                  <li>Clears the "Posted to Books" flag; PO status returns to Received</li>
                  <li>Serial ledger and GRN data are preserved</li>
                  <li>Global asset sequence counter is auto-resynced to max(remaining)</li>
                </ul>
              ) : (
                <ul className="list-disc pl-4 space-y-0.5">
                  <li>Clears the serial ledger, receipt spec, and received quantities</li>
                  <li>Reverts PO status to Issued</li>
                  <li>For Consumables POs, reverses the auto-inwarded stock</li>
                  <li>You will need to re-do the GRN using the new multi-serial dialog</li>
                </ul>
              )}
            </div>
            <div>
              <Label className="text-xs">Type <span className="font-mono font-semibold text-slate-900">{po.po_number}</span> to confirm</Label>
              <Input
                value={rollbackConfirm}
                onChange={(e) => setRollbackConfirm(e.target.value)}
                placeholder={po.po_number}
                className="mt-1 font-mono"
                data-testid="rollback-confirm-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRollbackKind(null); setRollbackConfirm(""); }}>
              Cancel
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700"
              disabled={busy || rollbackConfirm.trim() !== po.po_number}
              onClick={doRollback}
              data-testid="confirm-rollback-btn"
            >
              {busy ? "Rolling back..." : `Confirm ${rollbackKind === "posting" ? "Rollback Posting" : "Rollback GRN"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
